import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  NgModule,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CheckmarkLoaderModule } from '@commons/contact-modal/checkmark-loader/checkmark-loader.component';
import { InvalidControlScrollModule } from '@commons/directives/invalid-control-scroll/invalid-control-scroll.directive';
import { FormRgpdInfoModule } from '@commons/form-rgpd-info/form-rgpd-info.component';
import { WizValidators } from '@commons/validators';
import {
  DEFAULT_ZIPCODE_CONFIG,
  ZipcodeInputPoleEmploiModule,
} from '@commons/zipcode-input-pole-emploi/zipcode-input-pole-emploi.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { ContactLocalMissionWithZipcode } from '@models/event/contact';
import { PoleEmploiCity } from '@models/pole-emploi/city';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { LocalMissionWebservice } from '@webservices/local-mission/local-mission.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, finalize, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-cej-v2-form-contact-modal',
  templateUrl: './cej-v2-form-contact-modal.component.html',
  styleUrls: ['./cej-v2-form-contact-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CejV2FormContactModalComponent implements OnDestroy {
  @Output() tracker = new EventEmitter<void>();
  @Output() closeEvent = new EventEmitter<void>();

  form: FormGroup;

  loading$ = new BehaviorSubject<boolean>(false);
  loadingLoader$ = new BehaviorSubject<boolean>(false);

  ages = [...Array(15).keys()].map((item) => item + 16);
  zipCodeFieldConfig = { ...DEFAULT_ZIPCODE_CONFIG, required: true };

  get firstName(): FormControl {
    return this.form.get('firstName') as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get('lastName') as FormControl;
  }

  get mail(): FormControl {
    return this.form.get('mail') as FormControl;
  }

  get phone(): FormControl {
    return this.form.get('phone') as FormControl;
  }

  get age(): FormControl {
    return this.form.get('age') as FormControl;
  }

  get city(): FormControl {
    return this.form.get('city') as FormControl;
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  get styleCssCheckmarkLoader(): Observable<{
    width?: string;
    height?: string;
    logoWidth?: string;
    logoHeight?: string;
  }> {
    return this.isMobile$.pipe(
      filter((isMobile) => isMobile),
      map(() => {
        return {
          width: '4rem',
          height: '4rem',
          logoWidth: '2.5em',
          logoHeight: '2.5em',
        };
      })
    );
  }

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly cdr: ChangeDetectorRef,
    readonly formBuilder: FormBuilder,
    private readonly localMissionWebservice: LocalMissionWebservice,
    private readonly breakpointsService: BreakpointsService,
    private readonly scrollToService: ScrollToService
  ) {
    this.form = this.formBuilder.group({
      firstName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      lastName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      mail: [undefined, [Validators.required, WizValidators.noWhitespaceValidator, WizValidators.email]],
      phone: [undefined, [Validators.required, WizValidators.phone]],
      age: [undefined, [Validators.required]],
      city: [undefined, [Validators.required]],
    });
  }

  trackByAge = (_: number, age: number): number => age;

  submitForm(): void {
    if (this.form.invalid) {
      return;
    }

    this.loading$.next(true);
    this.loadingLoader$.next(true);

    const city: PoleEmploiCity = this.city.value;

    const contactLocalMission: ContactLocalMissionWithZipcode = {
      firstName: this.firstName.value,
      lastName: this.lastName.value,
      phoneNumber: this.phone.value,
      age: this.age.value,
      email: this.mail.value,
      location: {
        zipCode: city?.codePostal,
        geoPoint: city?.geoPoint,
      },
    };

    this.localMissionWebservice
      .sendContactMailWithZipcode(contactLocalMission)
      .pipe(
        finalize(() => {
          this.tracker.emit();
          this.endContact();
        })
      )
      .subscribe();
  }

  private endContact(): void {
    this.window.setTimeout(() => {
      this.loadingLoader$.next(false);
      this.cdr.detectChanges();

      this.scrollToService.scrollTo({
        target: '#loader-result',
        offset: -300,
      });
    }, 1000);
  }

  resetForm(): void {
    this.closeEvent.emit();
  }

  ngOnDestroy(): void {
    this.loading$.complete();
    this.loadingLoader$.complete();
  }
}

@NgModule({
  imports: [
    CheckmarkLoaderModule,
    CommonModule,
    FormRgpdInfoModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    ZipcodeInputPoleEmploiModule,
    InvalidControlScrollModule,
  ],
  exports: [CejV2FormContactModalComponent],
  declarations: [CejV2FormContactModalComponent],
  entryComponents: [CejV2FormContactModalComponent],
})
export class CejV2FormContactModalModule {}
